import { useIntl } from 'react-intl'

import { ApiError, RecordChangedError, SignInAgainError } from '@/shared/api/client'
import { Alert, Button } from '@/shared/ui'

/**
 * Says why a save failed: someone else changed the record (a 412), with a reload; fields to fix;
 * a sign-in to renew; the API out of reach; or anything else. Announced as it appears. `null` shows nothing.
 */
export function SaveProblem(props: {
  readonly error: unknown
  /** Messages for fields the form does not show, from `applyServerErrors`. */
  readonly unplaced?: readonly string[]
  /** Loads the record again, dropping the edits; offered after a 412. */
  readonly onReload?: () => void
  /** The text for a refusal the API names by its code alone, shown in place of the general one. */
  readonly refusals?: Readonly<Record<string, string>>
  readonly className?: string
}) {
  const intl = useIntl()
  const { error, onReload } = props
  if (error === null || error === undefined) {
    return null
  }

  const unplaced = props.unplaced ?? []
  const reference = error instanceof ApiError ? error.correlationId : null
  let titleId = 'forms.error.failed'
  let bodyId: string | null = null
  if (error instanceof RecordChangedError) {
    titleId = 'forms.error.changed.title'
    bodyId = 'forms.error.changed.body'
  } else if (error instanceof ApiError && Object.keys(error.fieldErrors).length > 0) {
    titleId = 'forms.error.fields'
  } else if (error instanceof SignInAgainError) {
    titleId = 'forms.error.signInAgain'
  } else if (error instanceof ApiError && error.status === 0) {
    titleId = 'forms.error.offline'
  }
  const refusals = props.refusals ?? {}
  const refusal = error instanceof ApiError && error.code !== null && Object.hasOwn(refusals, error.code) ? refusals[error.code] : undefined

  return (
    <Alert
      severity="error"
      title={refusal ?? intl.formatMessage({ id: titleId })}
      {...(props.className === undefined ? {} : { className: props.className })}
    >
      {bodyId !== null && <p className="mt-1">{intl.formatMessage({ id: bodyId })}</p>}
      {unplaced.length > 0 && (
        <ul className="mt-1 list-inside list-disc">
          {unplaced.map((message, index) => (
            // Two fields can have the same message, such as "Required."
            <li key={`${String(index)}-${message}`}>{message}</li>
          ))}
        </ul>
      )}
      {reference !== null && !(error instanceof RecordChangedError) && (
        <p className="mt-1 text-xs">{intl.formatMessage({ id: 'forms.error.reference' }, { reference })}</p>
      )}
      {error instanceof RecordChangedError && onReload !== undefined && (
        <Button variant="outlined" size="small" onClick={onReload} className="mt-3">
          {intl.formatMessage({ id: 'forms.error.changed.reload' })}
        </Button>
      )}
    </Alert>
  )
}
